import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Chip,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FileUploader } from '../components/upload/FileUploader';
import { TaskSelector } from '../components/upload/TaskSelector';
import { UploadProgress } from '../components/upload/UploadProgress';
import { useAnalysisContext } from '../context/AnalysisContext';
import { useAuth } from '../context/AuthContext';
import { apiConfig } from '../config/api';

const STYLE_GUIDE_TASK_ID = 'style-guide-adherence';

const steps = ['Select File', 'Choose Analysis Tasks', 'Upload & Analyze'];

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedTasks, promptOverrides, loadTasks, clearPromptOverrides, loadDefaultPrompts, toggleTask } = useAnalysisContext();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedStyleGuide, setSelectedStyleGuide] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deckId, setDeckId] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Reset selections and prompt overrides for a new upload run
  useEffect(() => {
    clearPromptOverrides();
    loadTasks();
    loadDefaultPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-add/remove style-guide-adherence task based on style guide selection
  useEffect(() => {
    const hasStyleGuideTask = selectedTasks.includes(STYLE_GUIDE_TASK_ID);
    if (selectedStyleGuide && !hasStyleGuideTask) {
      toggleTask(STYLE_GUIDE_TASK_ID);
    } else if (!selectedStyleGuide && hasStyleGuideTask) {
      toggleTask(STYLE_GUIDE_TASK_ID);
    }
  }, [selectedStyleGuide]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleUpload();
    } else {
      if (activeStep === 0) {
        clearPromptOverrides();
        loadDefaultPrompts();
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;

    setUploadStatus('uploading');
    setError(undefined);

    try {
      // Build request body
      const requestBody: Record<string, unknown> = {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        selectedTasks: selectedTasks,
        promptOverrides: promptOverrides,
      };

      if (selectedStyleGuide) {
        requestBody.styleGuideFileName = selectedStyleGuide.name;
        requestBody.styleGuideFileSize = selectedStyleGuide.size;
      }

      // Step 1: Get presigned upload URL(s) from API
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.upload}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to initiate upload');
      }

      const uploadData = await response.json();
      const { deckId: newDeckId, uploadUrl, uploadFields, styleGuideUploadUrl, styleGuideUploadFields } = uploadData.data;
      setDeckId(newDeckId);

      // Step 2a: Upload style guide to S3 first (if provided)
      if (selectedStyleGuide && styleGuideUploadUrl && styleGuideUploadFields) {
        const sgFormData = new FormData();
        Object.entries(styleGuideUploadFields).forEach(([key, value]) => {
          sgFormData.append(key, value as string);
        });
        sgFormData.append('file', selectedStyleGuide);

        const sgResponse = await fetch(styleGuideUploadUrl, {
          method: 'POST',
          body: sgFormData,
        });

        if (!sgResponse.ok && sgResponse.status !== 204) {
          throw new Error('Failed to upload style guide to storage');
        }
      }

      // Step 2b: Upload PPT file to S3 using presigned URL
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', selectedFile);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadStatus('success');
        } else {
          setError('Failed to upload file to storage');
          setUploadStatus('error');
        }
      });

      xhr.addEventListener('error', () => {
        setError('Network error during file upload');
        setUploadStatus('error');
      });

      xhr.open('POST', uploadUrl);
      xhr.send(formData);
    } catch (err) {
      setError((err as Error).message);
      setUploadStatus('error');
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedFile !== null;
      case 1:
        return selectedTasks.length > 0;
      case 2:
        return selectedFile !== null && selectedTasks.length > 0 && uploadStatus !== 'uploading';
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <FileUploader
            onFileSelect={setSelectedFile}
            selectedFile={selectedFile}
            onStyleGuideSelect={setSelectedStyleGuide}
            selectedStyleGuide={selectedStyleGuide}
          />
        );
      case 1:
        return <TaskSelector hasStyleGuide={selectedStyleGuide !== null} />;
      case 2:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ready to Upload
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                File: {selectedFile?.name}
              </Typography>
              {selectedStyleGuide && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Style Guide: {selectedStyleGuide.name}{' '}
                  <Chip label="Style Guide Adherence will run" size="small" color="info" variant="outlined" />
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Tasks: {selectedTasks.length} selected
              </Typography>
            </Paper>
            <UploadProgress
              status={uploadStatus}
              progress={uploadProgress}
              deckId={deckId}
              error={error}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Upload Presentation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload your PowerPoint file and select the analysis tasks you want to run
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            disabled={activeStep === 0 || uploadStatus === 'uploading'}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>

          {uploadStatus === 'success' && deckId ? (
            <Button
              variant="contained"
              onClick={() => navigate(`/analysis/${deckId}`)}
            >
              View Analysis
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed() || uploadStatus === 'uploading'}
              endIcon={activeStep === steps.length - 1 ? undefined : <ArrowForward />}
            >
              {activeStep === steps.length - 1 ? 'Upload' : 'Next'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};
